import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {
    ConfiguratorDatastoreService,
    ConfiguratorI18nCacheService,
    ConfiguratorTemplateHtmlService,
    DatastoreDto,
    I18nStringItem,
    Partner,
    TemplateHtmlDTO
} from '@tj/tj-dmp-lib-api';
import {MenuItem, MessageService, SelectItem} from 'primeng/api';

@Component({
    templateUrl: './etl-dictionnary-edit.component.html',
    styleUrls: ['./etl-dictionnary-edit.component.scss'],
    standalone: false
})
export class EtlDictionnaryEditComponent implements OnInit {

    breadcrumb: MenuItem[] = [{label: 'Marketing'}, {label: 'Bloc de template'}, {label: 'Création'}];
    description: string;

    actual: TemplateHtmlDTO;
    partner: Partner;
    form: UntypedFormGroup;
    submitted = false;
    codeAffiliations = {};
    statusAffiliations = {};
    translations: I18nStringItem[];
    affiliations: SelectItem[];
    defaultAffiliation = {code: 'DEFAULT', value: 'Défaut'} as const;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor: any;

    constructor(
        private fb: UntypedFormBuilder,
        protected templateHtmlService: ConfiguratorTemplateHtmlService,
        private route: ActivatedRoute,
        protected datastoreResourceService: ConfiguratorDatastoreService,
        protected i18nCacheService: ConfiguratorI18nCacheService,
        private messageService: MessageService,
        private router: Router
    ) {
    }

    _group: DatastoreDto;

    get group() {
        return this._group;
    }

    set group(group: DatastoreDto) {
        this._group = group;
        this.initOnUpdate();
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get currentCode() {
        return 'TPL_' + (this.form.value.name as string ?? '').toUpperCase().replace(/ /, '-');
    }

    get code() {
        return this.codeAffiliations[this.form.value.affiliation.code];
    }

    set code(code: string) {
        this.codeAffiliations[this.form.value.affiliation.code] = code;
    }

    get status() {
        return this.statusAffiliations[this.form.value.affiliation.code];
    }

    set status(status: string) {
        this.statusAffiliations[this.form.value.affiliation.code] = status;
    }

    ngOnInit() {
        this.description = 'Création d\'un bloc de template';
        this.form = this.fb.group({
            name: ['', Validators.required],
            description: [''],
            enabled: [true, Validators.required],
            affiliation: [this.defaultAffiliation],
        });

        this.i18nCacheService.searchClientUsingGET().subscribe(res => {
            this.affiliations = [this.defaultAffiliation, ...res.map(r => ({value: r.value, code: r.key} as SelectItem<string>))];
        });
    }

    initOnUpdate() {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.description = 'Edition d\'un bloc de template';
            this.breadcrumb[2].label = 'Édition';
            this.templateHtmlService.getTemplateHtmlUsingGET(this.group.code, id).subscribe((res) => {
                this.actual = res;
                this.translations = this.actual.dictionary;
                this.code = res.defaultHtml;
                this.form = this.fb.group({
                    name: [this.actual.name, Validators.required],
                    description: [this.actual.description],
                    enabled: [this.actual.enable, Validators.required],
                    affiliation: [this.defaultAffiliation],
                });
            });
        }
    }

    submit() {
        this.submitted = true;
        const status = this.form.status;
        if (status === 'INVALID') {
            this.messageService.add({severity: 'error', summary: 'Erreur', detail: 'Le bloc n\'a pas été créé, vérifiez vos champs'});
            return;
        }
        const value = this.form.value;
        const html = {...this.codeAffiliations, [this.defaultAffiliation.code]: undefined};
        Object.entries(this.statusAffiliations).forEach(([code, status]) => {
            if (status) {
                html[code] = null;
            }
        });
        const bloc: TemplateHtmlDTO = {
            code: this.currentCode,
            defaultHtml: this.codeAffiliations[this.defaultAffiliation.code],
            htmlByAffi: html,
            description: value.description,
            dictionary: this.translations,
            enable: value.enabled,
            name: value.name,
            partner: this.partner.code,
            tenant: this.group.code,
            templateEngine: 'jtwig'
        };

        if (!this.actual) {
            this.templateHtmlService.createTemplateHtmlUsingPOST(this.group.code, bloc).subscribe(() => {
                const navigationExtras: NavigationExtras = {state: {message: 'success_created'}};
                this.router.navigate([`/h/marketing/template`], navigationExtras);
            }, (e) => {
                this.messageService.add({severity: 'error', summary: 'Erreur', detail: `Un problème est survenu: ${e.toString()}`});
            });
        } else {
            bloc.id = this.actual.id;
            this.templateHtmlService.updateTemplateHtmlUsingPUT(this.group.code, bloc).subscribe(() => {
                const navigationExtras: NavigationExtras = {state: {message: 'success_updated'}};
                this.router.navigate([`/h/marketing/template`], navigationExtras);
            }, (e) => {
                this.messageService.add({severity: 'error', summary: 'Erreur', detail: `Un problème est survenu: ${e.toString()}`});
            });
        }
    }

    appendKeyInCode(key: string) {
        const pos = this.editor.getPosition() as { lineNumber: number, column: number };
        const array = (this.code ?? '').split('\n');
        array[pos.lineNumber - 1] = array[pos.lineNumber - 1].slice(0, pos.column - 1) + `{{${key}}}` + array[pos.lineNumber - 1].slice(pos.column - 1);
        this.code = array.join('\n');
    }
}
